import React, { useEffect, useState } from 'react';
import { Modal, Form, Col, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import BeSafeControlFeedback from 'components/commons/BeSafeControlFeedback';
import BeSafeSwitch from 'components/commons/BeSafeSwitch';
import NetworksSelect from 'components/configuration/firewall/modals/NetworksSelect';
import { Formik } from 'formik';
import {
  createAgentProfile,
  patchAgentProfile,
} from 'app/crud/agent_profiles.crud';
import ConfigurationSection from 'components/devices/configuration/common/ConfigurationSection';
import { successToast, errorToast } from 'utils/toastUtils';
import BeSafeButton from 'components/commons/BeSafeButton';
import { shallowEqual, useSelector } from 'react-redux';
import { UDP_PORT } from 'utils/utils';
import { getCertificate } from 'app/crud/certificates.crud';
import CertificateSelect from 'components/commons/CertificateSelect';

const AddAgentProfileModal = ({
  editMode,
  selectedAgentProfile,
  showModal,
  onHide,
  onCreate,
  onUpdate,
}) => {
  const intl = useIntl();
  const [certificate, setCertificate] = useState(null);

  const { accountID: reduxAccountID } = useSelector(
    ({ auth }) => ({
      accountID: auth.user.account_id,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (editMode && selectedAgentProfile?.certificate_id) {
      getCertificate(selectedAgentProfile.certificate_id)
        .then((response) => {
          let certificate = response.data.certificate;

          if (certificate) {
            setCertificate(certificate);
          }
        })
        .catch((err) => {
          errorToast({
            body: 'SSL_INSPECTION.CERTIFICATE_ERROR_FETCH',
            intl: intl,
          });
        });
    }
  }, [editMode, selectedAgentProfile?.certificate_id, intl]);

  const initialNetworkValues = {
    name: editMode ? selectedAgentProfile?.name : '',
    host: editMode ? selectedAgentProfile?.host : '',
    protocol: editMode ? selectedAgentProfile?.protocol : 'tcp',
    port: editMode ? selectedAgentProfile?.port : UDP_PORT,
    encrypted: editMode ? selectedAgentProfile?.encrypted : false,
    trust_unknown_ca: editMode ? selectedAgentProfile?.trust_unknown_ca : false,
    certificate_id: editMode ? selectedAgentProfile?.certificate_id : '',
    antivirus: editMode
      ? selectedAgentProfile?.is_antivirus_host_checker_enabled
      : false,
    corporate_network: editMode
      ? selectedAgentProfile?.is_corporate_network_enabled
      : false,
    routing_address: editMode ? selectedAgentProfile?.network_objects : [],
    enable_netflow: editMode
      ? selectedAgentProfile?.enable_netflow ?? false
      : false,
    sampling_rate: editMode ? selectedAgentProfile?.sampling_rate ?? 100 : 100,
    export_frequency: editMode
      ? selectedAgentProfile?.export_frequency ?? 60
      : 60,
  };

  return (
    <Modal show={showModal} onHide={onHide} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title>
          {editMode ? (
            <FormattedMessage id="AGENTPROFILE.EDIT" />
          ) : (
            <FormattedMessage id="AGENTPROFILE.ADD" />
          )}
        </Modal.Title>
      </Modal.Header>

      <Formik
        initialValues={{ ...initialNetworkValues }}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(true);

          let params = {
            agent_profile: {
              name: values.name,
              is_antivirus_host_checker_enabled: values.antivirus,
              is_corporate_network_enabled: values.corporate_network,
              networks: values.routing_address,
              enable_netflow: values.enable_netflow,
              host: values.host,
              protocol: values.protocol,
              port: values.port,
              encrypted: values.encrypted,
              trust_unknown_ca: values.trust_unknown_ca,
              certificate_id: values.certificate_id || null,
              sampling_rate: values.sampling_rate,
              export_frequency: values.export_frequency,
            },
          };

          if (values.trust_unknown_ca || !values.encrypted) {
            params.agent_profile.certificate_id = null;
            setCertificate(null);
          }

          if (editMode) {
            patchAgentProfile(
              params,
              selectedAgentProfile.id,
              selectedAgentProfile.account_id
            )
              .then(() => {
                onUpdate(params.agent_profile);
                successToast({
                  body: 'AGENTPROFILE.EDIT_SUCCESS',
                  intl: intl,
                });
              })
              .catch((err) => {
                errorToast({ body: err.response.data?.error, intl: intl });
              })
              .finally(() => {
                setSubmitting(false);
              });
          } else {
            createAgentProfile(params, reduxAccountID)
              .then(() => {
                onCreate();
                successToast({
                  body: 'AGENTPROFILE.ADD_SUCCESS',
                  intl: intl,
                });
              })
              .catch((err) => {
                errorToast({ body: err.response.data?.error, intl: intl });
              })
              .finally(() => {
                setSubmitting(false);
              });
          }
        }}
        validate={(values) => {
          const errors = {};
          if (!values.name || !values.name.trim()) {
            errors.name = intl.formatMessage({ id: 'GENERAL.REQUIRED_FIELD' });
          }
          if (values.name.length > 64) {
            errors.name = intl.formatMessage({ id: 'GENERAL.MAX_NAME_SIZE' });
          }

          // Only validate NetFlow fields if BOTH env and enable_netflow are true
          if (values.enable_netflow) {
            if (!values.host || !values.host.trim()) {
              errors.host = intl.formatMessage({
                id: 'GENERAL.REQUIRED_FIELD',
              });
            }
            if (!values.port) {
              errors.port = intl.formatMessage({
                id: 'GENERAL.REQUIRED_FIELD',
              });
            } else if (typeof values.port === 'string' && !values.port.trim()) {
              errors.port = intl.formatMessage({
                id: 'GENERAL.REQUIRED_FIELD',
              });
            }
            if (
              values.encrypted &&
              !values.trust_unknown_ca &&
              !values.certificate_id
            ) {
              errors.certificate_id = intl.formatMessage({
                id: 'GENERAL.REQUIRED_FIELD',
              });
            }
            if (
              values.sampling_rate === undefined ||
              values.sampling_rate === null ||
              values.sampling_rate === ''
            ) {
              errors.sampling_rate = intl.formatMessage({
                id: 'GENERAL.REQUIRED_FIELD',
              });
            } else if (
              isNaN(values.sampling_rate) ||
              Number(values.sampling_rate) < 0.01 ||
              Number(values.sampling_rate) > 100
            ) {
              errors.sampling_rate = intl.formatMessage({
                id: 'AGENTPROFILE.INVALID_SAMPLING_RATE',
              });
            }
            if (
              values.export_frequency === undefined ||
              values.export_frequency === null ||
              values.export_frequency === ''
            ) {
              errors.export_frequency = intl.formatMessage({
                id: 'GENERAL.REQUIRED_FIELD',
              });
            } else if (
              isNaN(values.export_frequency) ||
              !Number.isInteger(Number(values.export_frequency)) ||
              Number(values.export_frequency) <= 0 ||
              Number(values.export_frequency) > 86400
            ) {
              errors.export_frequency = intl.formatMessage({
                id: 'AGENTPROFILE.INVALID_EXPORT_FREQUENCY',
              });
            }
          }
          return errors;
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          handleBlur,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Modal.Body>
              <Row>
                <Col md={6}>
                  <Form.Group controlId="name">
                    <Form.Label>
                      <FormattedMessage id="GENERAL.NAME" />
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      onChange={handleChange}
                      isInvalid={errors.name}
                      className="border-secondary"
                      placeholder={intl.formatMessage({
                        id: 'GENERAL.NAME_PLACEHOLDER',
                      })}
                      value={values.name}
                      required
                    />
                    {errors.name && touched.name && (
                      <Form.Control.Feedback type="invalid">
                        {errors.name}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <ConfigurationSection
                className="d-flex flex-column h-100"
                title={<FormattedMessage id="AGENTPROFILE.NETFLOW_SECTION" />}
                headerButtons={() => {}}
                handleSubmit={() => {}}
              >
                <>
                  <Row>
                    <Col>
                      <Form.Group as={Row} className="align-items-center mb-3">
                        <Col md={4}>
                          <Form.Label className="mb-0">
                            <FormattedMessage id="AGENTPROFILE.ENABLE_NETFLOW" />
                          </Form.Label>
                        </Col>
                        <Col md={8}>
                          <BeSafeSwitch
                            state={values.enable_netflow}
                            name="enable_netflow"
                            onChange={(val) => {
                              setFieldValue('enable_netflow', val);
                            }}
                          />
                        </Col>
                      </Form.Group>
                    </Col>
                  </Row>
                  {values.enable_netflow && (
                    <>
                      <Row>
                        <Col md={6}>
                          <Form.Group controlId="host">
                            <Form.Label>
                              <FormattedMessage id="AGENTPROFILE.HOST" />
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="host"
                              value={values.host}
                              className="border-secondary"
                              onChange={handleChange}
                              isInvalid={touched.host && errors.host}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.host}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="protocol">
                            <Form.Label>
                              <FormattedMessage id="AGENTPROFILE.PROTOCOL" />
                            </Form.Label>
                            <Form.Control
                              as="select"
                              className="border-secondary"
                              value={values.protocol}
                              name="protocol"
                              onChange={handleChange}
                            >
                              <option value="tcp">TCP</option>
                              <option value="udp">UDP</option>
                            </Form.Control>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group controlId="port">
                            <Form.Label>
                              <FormattedMessage id="AGENTPROFILE.PORT" />
                            </Form.Label>
                            <Form.Control
                              type="number"
                              value={values.port}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.port && errors.port}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.port}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Form.Group
                            as={Row}
                            className="align-items-center mb-3"
                          >
                            <Col md={4}>
                              <Form.Label className="mb-0">
                                <FormattedMessage id="AGENTPROFILE.ENCRYPTED" />
                              </Form.Label>
                            </Col>
                            <Col md={8}>
                              <BeSafeSwitch
                                state={values.encrypted}
                                name="encrypted"
                                onChange={(val) => {
                                  setFieldValue('encrypted', val);
                                  if (!val) {
                                    setFieldValue('certificate_id', '');
                                    setCertificate(null);
                                  }
                                }}
                              />
                            </Col>
                          </Form.Group>
                        </Col>
                      </Row>
                      {values.encrypted && (
                        <Row>
                          <Col>
                            <Form.Group
                              as={Row}
                              className="align-items-center mb-3"
                            >
                              <Col md={4}>
                                <Form.Label className="mb-0">
                                  <FormattedMessage id="AGENTPROFILE.TRUST_UNKNOWN_CA" />
                                </Form.Label>
                              </Col>
                              <Col md={8}>
                                <BeSafeSwitch
                                  state={values.trust_unknown_ca}
                                  name="trust_unknown_ca"
                                  onChange={(val) => {
                                    setFieldValue('trust_unknown_ca', val);
                                    if (val) {
                                      setFieldValue('certificate_id', '');
                                      setCertificate(null);
                                    }
                                  }}
                                />
                              </Col>
                            </Form.Group>
                          </Col>
                        </Row>
                      )}
                      <Row>
                        {!values.trust_unknown_ca && values.encrypted && (
                          <Form.Group>
                            <Col>
                              <CertificateSelect
                                handleChange={(val) => {
                                  setFieldValue('certificate_id', val.id);
                                  setCertificate(val);
                                }}
                                initialTag={editMode ? certificate : null}
                              />
                              {errors.certificate_id && (
                                <BeSafeControlFeedback>
                                  {errors.certificate_id}
                                </BeSafeControlFeedback>
                              )}
                            </Col>
                          </Form.Group>
                        )}
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group controlId="sampling_rate">
                            <Form.Label>
                              <FormattedMessage id="AGENTPROFILE.SAMPLING_RATE" />
                            </Form.Label>
                            <Form.Control
                              type="number"
                              step="0.01"
                              min="0.01"
                              max="100"
                              name="sampling_rate"
                              value={values.sampling_rate}
                              onChange={handleChange}
                              isInvalid={
                                touched.sampling_rate && errors.sampling_rate
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.sampling_rate}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="export_frequency">
                            <Form.Label>
                              <FormattedMessage id="AGENTPROFILE.EXPORT_FREQUENCY" />
                            </Form.Label>
                            <Form.Control
                              type="number"
                              min="1"
                              step="1"
                              name="export_frequency"
                              value={values.export_frequency}
                              onChange={handleChange}
                              isInvalid={
                                touched.export_frequency &&
                                errors.export_frequency
                              }
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.export_frequency}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              </ConfigurationSection>

              <ConfigurationSection
                className="d-flex flex-column h-100"
                title={
                  <FormattedMessage id="AGENTPROFILE.HOST_CHECKER_SECTION" />
                }
                headerButtons={() => {}}
                handleSubmit={() => {}}
              >
                <Row>
                  <Col>
                    <Form.Group as={Row} className="align-items-center mb-3">
                      <Col md={4}>
                        <Form.Label className="mb-0">
                          <FormattedMessage id="AGENTPROFILE.ANTIVIRUS" />
                        </Form.Label>
                      </Col>
                      <Col md={8}>
                        <BeSafeSwitch
                          state={values.antivirus}
                          name="Antivirus"
                          onChange={(val) => {
                            setFieldValue('antivirus', val);
                          }}
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                </Row>
              </ConfigurationSection>

              <ConfigurationSection
                className="d-flex flex-column h-100"
                title={
                  <FormattedMessage id="AGENTPROFILE.LOCATION_AWARENESS_SECTION" />
                }
                headerButtons={() => {}}
                handleSubmit={() => {}}
              >
                <Row>
                  <Col>
                    <Form.Group
                      as={Row}
                      controlId="corporate_network"
                      className="align-items-center mb-3"
                    >
                      <Col md={4}>
                        <Form.Label className="mb-0">
                          <FormattedMessage id="AGENTPROFILE.COPORATE_NETWORK" />
                        </Form.Label>
                      </Col>
                      <Col md={8}>
                        <BeSafeSwitch
                          state={values.corporate_network}
                          name="corporate_network"
                          onChange={(val) => {
                            setFieldValue('corporate_network', val);
                          }}
                        />
                      </Col>
                    </Form.Group>

                    {values.corporate_network && (
                      <Form.Group controlId="routing_address">
                        <Form.Label>
                          <FormattedMessage id="AGENTPROFILE.IP_CONFIG" />
                        </Form.Label>
                        <span>
                          <NetworksSelect
                            handleChange={(val) =>
                              setFieldValue(
                                'routing_address',
                                val.map((v) => ({
                                  ...v,
                                  data: {
                                    ...v.data,
                                    network_type: v.data.network_type,
                                  },
                                }))
                              )
                            }
                            selectType="source"
                            allowedNetworks={['network', 'network-group']}
                            initialTags={values.routing_address}
                            ipProtocol="ipv4"
                          />
                        </span>
                        <BeSafeControlFeedback>
                          {errors.routing_address}
                        </BeSafeControlFeedback>
                      </Form.Group>
                    )}
                  </Col>
                </Row>
              </ConfigurationSection>
            </Modal.Body>
            <Modal.Footer className="py-3">
              <BeSafeButton
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                text={<FormattedMessage id="GENERAL.SAVE" />}
              />
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddAgentProfileModal;
